import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
   Grid,
   GridItem,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { renderFormControl, renderSelectFormControl } from 'utils/DynamicFormData';
import { getCities, addCity, updateCity } from '../../../../../redux/actions/city';
// import { getCountries } from '../../../../../redux/actions/country';
// import { getStates } from '../../../../../redux/actions/state';
import '../../../companyProfile/css.css';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { useGlobal } from '../../../../../contexts';

const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),
   stId: Yup.string().required('State is required'),
});

const CityMasterForm = ({ isOpen, onClose, isEditMode = false, demoname, currentRow = null, onSave, saveandcontinue = true }) => {
   const [row, setRow] = useState({
      name: '',
      stId: '',
      status: '',
   });

   const [errors, setErrors] = useState({});
   const toast = useToast();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const dispatch = useDispatch();
   const { stateList } = useGlobal();
   // const stateList = useSelector((state) => state.state.list);

   // useEffect(() => {
   //   if (isOpen) {
   //     dispatch(getCountries());
   //     dispatch(getStates());
   //   }
   // }, [dispatch, isOpen]);

   const clearFormData = () => {
      setRow({
         name: '',
         stId: '',
         status: '',
      });
      setErrors({});
   };

   useEffect(() => {
      if (demoname && row.name === '') {
         setRow((prev) => ({ ...prev, name: demoname }));
      }
      demoname = '';
   }, [demoname, row.name]);

   useEffect(() => {
      if (!isEditMode) {
         clearFormData();
      } else if (isEditMode && currentRow) {
         const selectedState = stateList?.find((state) => state.id === currentRow.stId);

         setRow({
            name: currentRow.name || '',
            stId: currentRow.stId || '',
            status: currentRow.status || '',
         });
      }
   }, [isEditMode, currentRow, stateList]);

   const validateForm = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setRow((prev) => {
         const updatedRow = { ...prev, [field]: value };
         return updatedRow;
      });

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const handleSubmit = async () => {
      const isValid = await validateForm();
      if (isValid) {
         const processedData = {
            ...row,
            status: row.status ? 1 : 0,
         };

         try {
            if (isEditMode && currentRow?.id) {
               processedData.id = currentRow.id;
               await dispatch(updateCity(processedData));
            } else {
               await dispatch(addCity(processedData));
            }
            dispatch(getCities());
            onClose();
         } catch (error) {
            toast({
               title: 'Error',
               description: error.message || 'An error occurred while saving the city.',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handlesavecontinue = async () => {
      const isValid = await validateForm();
      if (isValid) {
         const processedData = {
            ...row,
            status: row.status ? 1 : 0,
         };

         try {
            if (isEditMode && currentRow?.id) {
               processedData.id = currentRow.id;
               await dispatch(updateCity(processedData));
            } else {
               await dispatch(addCity(processedData));
            }
            dispatch(getCities());
            clearFormData();
         } catch (error) {
            toast({
               title: 'Error',
               description: error.message || 'An error occurred while saving the city.',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   return (
      <Modal isOpen={isOpen} onClose={handleCansel} size="4xl">
         <ModalOverlay />
         <ModalContent
            bg={useColorModeValue('white', '#2d3748')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="20px"
            p={0}
            mx="15px"
            mt={{ base: '45%', md: '25%', xl: '15%' }}
            boxShadow="lg">
            <ModalHeader fontSize="20px" fontWeight="600" mb={{ base: 0, xl: 4 }}>
               {isEditMode ? 'Edit City' : 'Add New City'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }} gap={2}>
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'name',
                        'Name',
                        {
                           value: row.name,
                           onChange: (e) => handleInputChange('name', e.target.value),
                        },
                        errors,
                        textColor,
                        'text',
                        true,
                     )}
                  </GridItem>
                  <GridItem className="seperator-width">
                     {renderDynamicDropdown(
                        'stId',
                        'State',
                        stateList,
                        {
                           value: row.stId,
                           onChange: (value) => handleInputChange('stId', value),
                           showCustomNoOptions: true,
                           openfrom: 'state',
                        },
                        errors,
                        textColor,
                        true,
                        'Select State',
                     )}
                  </GridItem>
               </Grid>
            </ModalBody>
            <ModalFooter>
               {!isEditMode && (saveandcontinue === true) && (
                  <Button onClick={handlesavecontinue} colorScheme="green" ml={3}>
                     Save & continue
                  </Button>
               )}
               <Button onClick={handleSubmit} colorScheme="green" ml={3}>
                  {isEditMode ? 'Update' : 'Save & Finish'}
               </Button>
               <Button variant="ghost" onClick={handleCansel} ml={3}>
                  Cancel
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default CityMasterForm;
